import React, { Component } from "react";
import { Form, Dropdown, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { PiEye, PiInfo } from "react-icons/pi";
import Pagination from "react-js-pagination";
import axios from "../../axios";
import axiosLib from "axios";
import apiError from "../../error";
import swalAlert from "../../swalFunction";
import { getDate } from "../../function";
import simulationandtrain from "../../assets/create_campaign/simulationandtrain.png";
import "../launch-modal/steps/steps.css";
import "./course-catalog.css";

const getCancelSource = () => {
	const CancelToken = axiosLib.CancelToken;
	return CancelToken ? CancelToken.source() : null;
};

const EMPTY_FILTERS = { courseType: "", languageId: "", difficulty: "", complianceIds: [], topicIds: [] };

/**
 * LMS course catalog — the selectable course card grid with Filters,
 * Single/Multiple assignment mode, search and pagination.
 *
 * Purely presentational with respect to selection: the selected ids live in the
 * parent, which is what lets the same grid back both the web launch wizard
 * (launch-modal/steps/training-selection-step.js) and the AI/MCP chat picker
 * (ai-components/ai-training-selection.js) without the two drifting apart.
 */
export default class CourseCatalog extends Component {
	constructor(props) {
		super(props);
		this.coursesCancel = null;
		this.state = {
			courseLoader: false,
			lmsCourses: [],
			count: 0,
			imageURL: "",
			previewURL: "",
			limit: props.limit || 12,
			activePage: 1,
			search: "",
			filters: { ...EMPTY_FILTERS },
			filterPanelOpen: false,
			filterTab: "courseType",
			filterDraft: { ...EMPTY_FILTERS },
			filterOptions: {
				// courseType is a fixed UI list (not data-driven). Lite/Advanced are
				// presentation labels that both route to the SCORM source and return
				// the same courses; Generic routes to the non-SCORM source.
				courseTypes: ["Generic", "Scorm Lite", "Scorm Advanced"],
				languages: [],
				difficulties: ["Beginner", "Intermediate", "Advanced"],
				compliances: [],
				topics: [],
			},
		};
	}

	componentDidMount() {
		this.loadCourses();
	}

	componentWillUnmount() {
		if (this.coursesCancel) this.coursesCancel();
	}

	loadCourses = () => {
		if (this.coursesCancel) this.coursesCancel();
		const source = getCancelSource();
		if (source) this.coursesCancel = source.cancel;
		const { filters } = this.state;
		/* Filter options come from this same API (no separate endpoint). Only an
		   UNFILTERED request reflects everything Krademy has, so capture options
		   from those responses only — a filtered response (e.g. courseType=Scorm)
		   would otherwise narrow the dropdowns to the current result set. */
		const isUnfilteredRequest = !this.state.search && !filters.courseType && !filters.languageId
			&& !filters.difficulty && !filters.complianceIds.length && !filters.topicIds.length;
		this.setState({ courseLoader: true });
		axios
			.get("/api/lms/course-list", {
				params: {
					limit: this.state.limit,
					page: this.state.activePage,
					search: this.state.search,
					courseType: filters.courseType || undefined,
					languageIds: filters.languageId || undefined,
					difficulty: filters.difficulty || undefined,
					complianceIds: filters.complianceIds.length ? filters.complianceIds.join(",") : undefined,
					topicIds: filters.topicIds.length ? filters.topicIds.join(",") : undefined,
				},
				cancelToken: source ? source.token : undefined,
			})
			.then((res) => {
				const courses = res.data.courses || [];
				const imageURL = res.data.imageURL || "";
				const previewURL = res.data.previewURL || "";
				this.setState({
					courseLoader: false,
					lmsCourses: courses,
					count: res.data.count || 0,
					imageURL,
					previewURL,
					...(res.data.filterOptions && isUnfilteredRequest ? {
						filterOptions: {
							// courseType is a fixed UI list (Generic / Scorm Lite / Scorm
							// Advanced) — never overwrite it from the API, which only
							// reports the source(s) that returned courses.
							courseTypes: this.state.filterOptions.courseTypes,
							languages: res.data.filterOptions.languages || [],
							difficulties: res.data.filterOptions.difficulties || this.state.filterOptions.difficulties,
							compliances: res.data.filterOptions.compliances || [],
							topics: res.data.filterOptions.topics || [],
						}
					} : {}),
				});
				/* Hand the name/logo lookup up so the parent can render selected
				   courses (chips, configure table, preview) without refetching. */
				if (this.props.onCoursesLoaded) {
					const courseName = {};
					courses.forEach((c) => { courseName[c.id] = { name: c.name, logo: c.logo }; });
					this.props.onCoursesLoaded({ courseName, imageURL, previewURL });
				}
			})
			.catch((err) => {
				if (!axiosLib.isCancel(err)) {
					this.setState({ courseLoader: false });
					apiError(err);
				}
			});
	};

	handleCoursePreview = (courseId) => {
		if (!courseId || !this.state.previewURL) return;
		document.body.style.cursor = "wait";
		axios
			.get("/api/lms", {
				params: { url: `${this.state.previewURL}${courseId}?r=Sw4s56g` },
			})
			.then((res) => {
				const allowedBaseURL = new URL(this.state.previewURL);
				const url = new URL(res.data.url);

				if (url.origin !== allowedBaseURL.origin) {
					swalAlert("Security Alert", "Invalid redirect URL detected!", "error");
					return;
				}

				const redirectUrl = url.searchParams.get("url");
				if (redirectUrl) {
					try {
						const parsedRedirectUrl = new URL(redirectUrl, allowedBaseURL);
						if (parsedRedirectUrl.origin !== allowedBaseURL.origin) {
							swalAlert("Security Alert", "Invalid redirect target detected!", "error");
							return;
						}
					} catch (error) {
						swalAlert("Security Alert", "Invalid redirect target detected!", "error");
						return;
					}
				}

				window.open(res.data.url, "_blank");
			})
			.catch((err) => apiError(err))
			.finally(() => {
				document.body.style.cursor = "";
			});
	};

	setFilterTab = (filterTab) => this.setState({ filterTab });

	handleDraftFilterChange = (key, value) => {
		this.setState((prev) => ({ filterDraft: { ...prev.filterDraft, [key]: value } }));
	};

	handleDraftMultiFilterToggle = (key, value) => {
		this.setState((prev) => {
			const arr = prev.filterDraft[key] || [];
			const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
			return { filterDraft: { ...prev.filterDraft, [key]: next } };
		});
	};

	applyFilters = () => {
		this.setState((prev) => ({
			filters: { ...prev.filterDraft },
			activePage: 1,
			filterPanelOpen: false,
		}), this.loadCourses);
	};

	/* clear a single filter (string filters -> "", multi-select -> []) */
	removeFilter = (key) => {
		const emptyValue = Array.isArray(this.state.filters[key]) ? [] : "";
		this.setState((prev) => ({ filters: { ...prev.filters, [key]: emptyValue }, activePage: 1 }), this.loadCourses);
	};

	clearAllFilters = () => {
		this.setState({ filters: { ...EMPTY_FILTERS }, activePage: 1 }, this.loadCourses);
	};

	/* build the list of active filters as { key, label } for the chips row */
	getActiveFilters = () => {
		const { filters, filterOptions } = this.state;
		const chips = [];
		if (filters.courseType) chips.push({ key: "courseType", label: `Course Type: ${filters.courseType}` });
		if (filters.languageId) {
			const lang = (filterOptions.languages || []).find((l) => String(l.id) === String(filters.languageId));
			chips.push({ key: "languageId", label: `Language: ${lang ? lang.name : filters.languageId}` });
		}
		if (filters.difficulty) chips.push({ key: "difficulty", label: `Difficulty: ${filters.difficulty}` });
		if (filters.complianceIds.length) chips.push({ key: "complianceIds", label: `Compliance: ${filters.complianceIds.length}` });
		if (filters.topicIds.length) chips.push({ key: "topicIds", label: `Topics: ${filters.topicIds.length}` });
		return chips;
	};

	renderActiveFilters() {
		const chips = this.getActiveFilters();
		if (!chips.length) return null;
		return (
			<div className="training-active-filters">
				{chips.map((chip) => (
					<span key={chip.key} className="training-filter-chip">
						{chip.label}
						<button type="button" className="training-filter-chip-remove" aria-label={`Remove ${chip.label}`}
							onClick={() => this.removeFilter(chip.key)}>×</button>
					</span>
				))}
				<button type="button" className="training-filter-clear-all" onClick={this.clearAllFilters}>Clear all</button>
			</div>
		);
	}

	renderFilterMenu() {
		const { filterDraft, filterOptions, filterTab } = this.state;
		const hasCompliances = filterOptions.compliances.length > 0;
		const hasTopics = filterOptions.topics.length > 0;
		return (
			<Dropdown
				className="category-filter-dropdown"
				show={this.state.filterPanelOpen}
				onToggle={(isOpen) => this.setState({
					filterPanelOpen: isOpen,
					...(isOpen ? { filterDraft: { ...this.state.filters } } : {}),
				})}
			>
				<Dropdown.Toggle variant="secondary" className="filter-select category-dropdown-toggle">
					Filters
				</Dropdown.Toggle>
				<Dropdown.Menu className="filter-panel-dropdown p-0" style={{ minWidth: "360px" }}>
					<div className="d-flex">
						<div className="filter-vertical-tabs border-right" style={{ width: "120px", minHeight: "220px" }}>
							<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "courseType" ? "active" : ""}`}
								onClick={() => this.setFilterTab("courseType")}>
								Course Type
							</button>
							<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "language" ? "active" : ""}`}
								onClick={() => this.setFilterTab("language")}>
								Language
							</button>
							<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "difficulty" ? "active" : ""}`}
								onClick={() => this.setFilterTab("difficulty")}>
								Difficulty
							</button>
							{hasCompliances && (
								<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "compliance" ? "active" : ""}`}
									onClick={() => this.setFilterTab("compliance")}>
									Compliance
								</button>
							)}
							{hasTopics && (
								<button type="button" className={`d-block w-100 vertical-tab-item text-left ${filterTab === "topics" ? "active" : ""}`}
									onClick={() => this.setFilterTab("topics")}>
									Topics
								</button>
							)}
						</div>
						<div className="filter-tab-content flex-grow-1" style={{ minHeight: "220px" }}>
							{filterTab === "courseType" && (
								<>
									<h6 className="mb-3 filter-heading">Select Course Type</h6>
									<div className="filter-content-wrapper">
										<Form.Control as="select" value={filterDraft.courseType} onChange={(e) => this.handleDraftFilterChange("courseType", e.target.value)}>
											<option value="">All</option>
											{filterOptions.courseTypes.map((c) => <option key={c} value={c}>{c}</option>)}
										</Form.Control>
									</div>
								</>
							)}
							{filterTab === "language" && (
								<>
									<h6 className="mb-3 filter-heading">Select Language</h6>
									<div className="filter-content-wrapper">
										<Form.Control as="select" value={filterDraft.languageId} onChange={(e) => this.handleDraftFilterChange("languageId", e.target.value)}>
											<option value="">All</option>
											{filterOptions.languages.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
										</Form.Control>
									</div>
								</>
							)}
							{filterTab === "difficulty" && (
								<>
									<h6 className="mb-3 filter-heading">Select Difficulty</h6>
									<div className="filter-content-wrapper">
										<Form.Control as="select" value={filterDraft.difficulty} onChange={(e) => this.handleDraftFilterChange("difficulty", e.target.value)}>
											<option value="">All</option>
											{filterOptions.difficulties.map((d) => <option key={d} value={d}>{d}</option>)}
										</Form.Control>
									</div>
								</>
							)}
							{filterTab === "compliance" && hasCompliances && (
								<>
									<h6 className="mb-3 filter-heading">Select Compliance</h6>
									<div className="filter-content-wrapper">
										{filterOptions.compliances.map((c) => (
											<Form.Check key={c.id} type="checkbox" label={c.name}
												checked={filterDraft.complianceIds.includes(String(c.id))}
												onChange={() => this.handleDraftMultiFilterToggle("complianceIds", String(c.id))} />
										))}
									</div>
								</>
							)}
							{filterTab === "topics" && hasTopics && (
								<>
									<h6 className="mb-3 filter-heading">Select Topics</h6>
									<div className="filter-content-wrapper">
										{filterOptions.topics.map((t) => (
											<Form.Check key={t.id} type="checkbox" label={t.name}
												checked={filterDraft.topicIds.includes(String(t.id))}
												onChange={() => this.handleDraftMultiFilterToggle("topicIds", String(t.id))} />
										))}
									</div>
								</>
							)}
						</div>
					</div>
					<div className="filter-panel-footer p-2 border-top d-flex justify-content-end">
						<Button variant="primary" size="md" onClick={this.applyFilters}>Apply</Button>
					</div>
				</Dropdown.Menu>
			</Dropdown>
		);
	}

	render() {
		const { lmsCourses, courseLoader, imageURL } = this.state;
		const {
			selectedCourses = [],
			trainingAssignmentMode = "single",
			onModeChange,
			onToggleCourse,
			showModeSelector = true,
			disabled = false,
		} = this.props;

		const toggle = (courseId) => {
			if (disabled || !onToggleCourse) return;
			onToggleCourse(courseId);
		};

		return (
			<>
				{/* Toolbar: Filter | Single/Multiple | Search */}
				<div className="template-selection-filters course-catalog-filters">
					<div className="filter-row">
						<Form.Group className="filter-group course-filter-group">
							<Form.Label className="filter-label">Filter</Form.Label>
							{this.renderFilterMenu()}
						</Form.Group>
						{showModeSelector && (
							<>
								<div className="filter-separator" />
								<Form.Group className="filter-group training-mode-filter-group">
									<Form.Label className="template-mode-label">Training Assignment</Form.Label>
									<div className="template-mode-options">
										<div className={`template-mode-option ${trainingAssignmentMode === "single" ? "selected" : ""}`} onClick={() => onModeChange && onModeChange("single")}>
											<Form.Check type="radio" name="trainingMode" onChange={() => onModeChange && onModeChange("single")} checked={trainingAssignmentMode === "single"}
												label={<span className="template-mode-label-wrap">Single <OverlayTrigger placement="top" overlay={<Tooltip>Assign one training course.</Tooltip>}><PiInfo className="template-mode-info-icon" /></OverlayTrigger></span>} />
										</div>
										<div className="filter-separator-in" />
										<div className={`template-mode-option ${trainingAssignmentMode === "multiple" ? "selected" : ""}`} onClick={() => onModeChange && onModeChange("multiple")}>
											<Form.Check type="radio" name="trainingMode" onChange={() => onModeChange && onModeChange("multiple")} checked={trainingAssignmentMode === "multiple"}
												label={<span className="template-mode-label-wrap">Multiple <OverlayTrigger placement="top" overlay={<Tooltip>Assign multiple training courses.</Tooltip>}><PiInfo className="template-mode-info-icon" /></OverlayTrigger></span>} />
										</div>
									</div>
								</Form.Group>
							</>
						)}
						<Form.Group className="template-search-group">
							<Form.Label className="filter-label">Search</Form.Label>
							<Form.Control type="search" className="template-search-input" placeholder="Search"
								value={this.state.search}
								onChange={(e) => this.setState({ search: e.target.value, activePage: 1 }, this.loadCourses)} />
						</Form.Group>
					</div>
				</div>

				{/* Active filter chips (removable) */}
				{this.renderActiveFilters()}

				{/* Course card grid */}
				{courseLoader ? (
					<div className="template-loading">Loading courses...</div>
				) : (
					<div className="template-grid">
						{lmsCourses.length > 0 ? lmsCourses.map((course, index) => {
							const isSelected = selectedCourses.indexOf(course.id) > -1;
							const thumb = course.logo ? imageURL + course.logo : simulationandtrain;
							return (
								<div key={course.id || index} className={`template-card ${isSelected ? "selected" : ""}`} onClick={() => toggle(course.id)}>
									<div className="template-card-image">
										<img src={thumb} alt={course.name || "Course"} className="img-fluid" onError={(e) => { e.target.src = simulationandtrain; }} />
										{course.courseType === "scorm" && <div className="template-simulation-badge">SCORM</div>}
										<div className="template-card-checkbox-wrapper">
											<Form.Check type="checkbox" checked={isSelected} disabled={disabled}
												onChange={(e) => { e.stopPropagation(); toggle(course.id); }}
												onClick={(e) => e.stopPropagation()}
												className="template-card-checkbox-input" />
										</div>
									</div>
									<div className="template-card-content">
										<div className="template-card-title-row">
											<h6 className="template-card-title">{course.name}</h6>
											<PiEye className="template-view-icon" title="Preview course"
												onClick={(e) => { e.stopPropagation(); this.handleCoursePreview(course.id); }} />
										</div>
										<p className="template-card-date">{getDate(course.createdAt)}</p>
										<div className="template-card-tags">
											{course.courseType === "scorm"
												? <span className="template-tag-category">SCORM</span>
												: <span className="template-tag-category">Generic</span>}
											{typeof course.totalLecture !== "undefined" && (
												<span className="template-tag-attack">{course.totalLecture} Lectures</span>
											)}
										</div>
									</div>
								</div>
							);
						}) : (
							<div className="template-empty">No courses found</div>
						)}
					</div>
				)}
				{this.state.count > this.state.limit && !courseLoader && (
					<div className="pagination-Wrap mt-2">
						<Pagination itemClass="page-item" linkClass="page-link"
							activePage={this.state.activePage} itemsCountPerPage={this.state.limit}
							totalItemsCount={this.state.count}
							onChange={(pageNumber) => this.setState({ activePage: pageNumber }, this.loadCourses)} />
					</div>
				)}
			</>
		);
	}
}
